import { useState } from 'react'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import useModalClose from 'hooks/useModalClose'
import { Button, CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IKitchenDish } from 'types/kitchen/kitchenDish.interface'
import { IKitchenTemplateEditForm } from 'types/kitchen/kitchenMenu.interface'
import { KITCHEN_WINDOWS } from '../kitchenData'
import { TemplateDayBtn } from '../Kitchen.styled'
import useFocusFirstInput from 'utils/useFocusFirstInput'

interface IProps {
  form: IKitchenTemplateEditForm
  dishes: IKitchenDish[]
  closeModal: () => void
  onSave: (form: IKitchenTemplateEditForm) => void
}

export const TemplateEditorModal = ({ form: initialForm, dishes, closeModal, onSave }: IProps) => {
  const [form, setForm] = useState(initialForm)
  const [tplDay, setTplDay] = useState(0)
  useModalClose(closeModal)
  useFocusFirstInput()

  const resizeCycle = (len: number) => {
    const c: IKitchenTemplateEditForm['cycle'] = {}
    KITCHEN_WINDOWS.forEach((w) => {
      c[w] = {}
      form.tagSets.forEach((t) => {
        const old = form.cycle[w]?.[t] || []
        c[w][t] = Array.from({ length: len }, (_, i) => old[i] ?? null)
      })
    })
    setForm({ ...form, length: len, cycle: c })
    setTplDay(Math.min(tplDay, len - 1))
  }

  const filledDays = Array.from({ length: form.length }, (_, i) =>
    KITCHEN_WINDOWS.some((w) => form.tagSets.some((t) => form.cycle[w]?.[t]?.[i])),
  )
  const completeDays = filledDays.filter(Boolean).length
  const canSave = form.name.trim() && completeDays > 0

  const lengthOptions = [7, 14, 28].map((n) => ({ label: `${n} days`, value: n }))

  return (
    <Modal black width='640px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>
            {form.id ? `Edit Template — ${form.name}` : 'New Menu Cycle Template'}
          </TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Flex gap='0.75rem' width='100%'>
          <Input
            width='100%'
            labelText='Template name'
            placeholder='e.g. Rainy Season 7-Day Cycle'
            value={form.name}
            onChange={(e: any) => setForm({ ...form, name: e.target.value })}
            nomarg
          />
          <HonestSelectInput
            width='100%'
            labelText='Cycle length'
            name='cycleLength'
            options={lengthOptions}
            defaultValue={form.length}
            onChange={(v: any) => resizeCycle(Number(v?.value || 14))}
            nomarg
          />
        </Flex>
        <OtherText grey>
          Cycle day — {completeDays}/{form.length} days have assignments
        </OtherText>
        <Flex wrap gap='0.3125rem'>
          {Array.from({ length: form.length }, (_, i) => (
            <TemplateDayBtn key={i} $active={tplDay === i} $filled={!!filledDays[i]} onClick={() => setTplDay(i)}>
              <BodyText
                weight={tplDay === i ? '700' : '500'}
                grey={!filledDays[i] && tplDay !== i}
                style={tplDay === i ? { color: '#1A1407' } : undefined}
              >
                {i + 1}
              </BodyText>
            </TemplateDayBtn>
          ))}
        </Flex>
        <Flex column gap='0.5rem' width='100%'>
          {KITCHEN_WINDOWS.map((w) => (
            <Flex key={w} gap='0.75rem' width='100%' wrap>
              <BodyText weight='700' style={{ width: '5rem' }}>
                {w}
              </BodyText>
              {form.tagSets.map((t) => {
                const legal = dishes.filter((d) => d.active && d.tags.includes(t))
                const dishOptions = [{ label: '—', value: null }, ...legal.map((d) => ({ label: d.name, value: d.id }))]
                const cur = form.cycle[w]?.[t]?.[tplDay]
                return (
                  <HonestSelectInput
                    key={`${w}-${t}-${tplDay}-${cur}`}
                    width='100%'
                    labelText={t}
                    name={`${w}-${t}`}
                    options={dishOptions}
                    defaultValue={cur || null}
                    onChange={(v: any) => {
                      const c = JSON.parse(JSON.stringify(form.cycle))
                      if (!c[w]) c[w] = {}
                      if (!c[w][t]) c[w][t] = Array(form.length).fill(null)
                      c[w][t][tplDay] = v?.value || null
                      setForm({ ...form, cycle: c })
                    }}
                    nomarg
                  />
                )
              })}
            </Flex>
          ))}
        </Flex>
        <Flex gap='0.5rem' width='100%'>
          <Button orangeBorder disabled={tplDay === 0} onClick={() => {
            const c = JSON.parse(JSON.stringify(form.cycle))
            KITCHEN_WINDOWS.forEach((w) =>
              form.tagSets.forEach((t) => {
                c[w][t][tplDay] = c[w][t][tplDay - 1]
              }),
            )
            setForm({ ...form, cycle: c })
          }}>
            Copy from day {tplDay}
          </Button>
        </Flex>
        <OtherText grey>
          Dish lists are filtered by dietary coverage per tag set. Empty slots are allowed — instantiation simply leaves those grid cells unassigned for manual planning.
        </OtherText>
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button onClick={closeModal}>Cancel</Button>
          <Button orange disabled={!canSave} onClick={() => onSave(form)}>
            {form.id ? 'Save Template' : 'Create Template'}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
